import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  ClarifyPayload, DiffDeltaPayload, DiffReadyPayload, DiffStatus, ProposedChange,
  ToolActivity, ToolCallPayload, ToolResultPayload, UIMessage, UsagePayload,
} from '@/types/chat';
import type { Provider } from '@/types/api';
import { getConversation } from '@/services/chat';

// LoadingPhase narrows the loading animation shown while the agent is
// working. Each phase maps to a distinct SVG animation in LoadingPulse —
// the user sees what the agent is *doing* without us having to surface
// raw thinking text. Null = no animation visible.
//
// Mapping rule: derive from the most recent tool_call (deterministic),
// falling back to "thinking" on bare reasoning/thinking events that
// don't carry tool metadata.
export type LoadingPhase =
  | 'thinking'   // pre-tool reasoning, planning, ask_clarification
  | 'reading'    // read_workbook (any action)
  | 'writing'    // cell_op / structure_op / sheet_op / format_op / data_op / annotate_op
  | 'creating'   // visual_op (charts + pivots)
  | 'searching'  // web_op
  | 'computing'; // analysis_op / compute_stats

interface ChatState {
  provider: Provider;
  model: string;
  messages: UIMessage[];
  conversationId: string | null;
  streaming: boolean;
  // Loading visibility is split: `loadingPhase` drives WHICH animation
  // renders; `thinkingText` is retained only for telemetry / a11y label.
  // The UI no longer surfaces the raw thinking string — animations
  // communicate progress instead.
  loadingPhase: LoadingPhase | null;
  thinkingText: string | null;
  // Phase 5 aggregates — cheap UI-side session counters.
  sessionCostUSD: number;
  sessionTokens: number;
  // Auto-Build (1-click dashboard) — UI-reactive state only; the loop
  // control lives in refs inside ChatInterface to avoid stale closures.
  autoBuild: { active: boolean; phase: 'continuing' | 'applying' | null; round: number; max: number } | null;
  // Live progress for the current apply ("Applying 3 of 8 — Drawing chart").
  applyProgress: { messageId: string; done: number; total: number; label: string } | null;
  // Set by a manual Apply of a partial_budget turn → ChatInterface reacts by
  // arming the auto-continue loop. Carries the just-applied message id.
  pendingAutoContinue: string | null;
  // Incremented by a reject (or any cross-component stop) so ChatInterface
  // can tear down its auto-build refs (which other components can't touch).
  autoBuildStopSeq: number;
  // Retry-after-failure: MessageBubble bumps the seq via requestRetry();
  // ChatInterface reacts by re-sending retryContent as a fresh turn (new
  // idempotency key). Nonce pattern for the same stale-closure reason as
  // autoBuildStopSeq.
  retrySeq: number;
  retryContent: string | null;

  setProviderModel: (provider: Provider, model: string) => void;
  setConversation: (id: string | null) => void;
  appendUserMessage: (content: string) => void;
  startAssistantStream: () => string;
  appendAssistantDelta: (chunk: string) => void;
  attachToolCall: (p: ToolCallPayload) => void;
  attachToolResult: (p: ToolResultPayload) => void;
  attachDiffDelta: (p: DiffDeltaPayload) => void;
  attachDiff: (p: DiffReadyPayload) => void;
  toggleDiffChange: (messageId: string, changeId: string) => void;
  setDiffAllSelected: (messageId: string, selected: boolean) => void;
  setDiffStatus: (messageId: string, status: DiffStatus, appliedIds?: string[]) => void;
  attachClarify: (p: ClarifyPayload) => void;
  resolveClarify: (messageId: string, interpretation: string) => void;
  dismissClarify: (messageId: string) => void;
  finalizeAssistant: (usage: UsagePayload | undefined, finalId?: string) => void;
  failAssistant: (message: string) => void;
  setSuggestions: (messageId: string, items: string[]) => void;
  setMessageFinishReason: (messageId: string, reason: string | undefined) => void;
  requestRetry: () => void;
  setAutoBuild: (v: ChatState['autoBuild']) => void;
  setApplyProgress: (v: ChatState['applyProgress']) => void;
  setPendingAutoContinue: (messageId: string | null) => void;
  stopAutoBuild: () => void;
  setThinking: (msg: string | null) => void;
  setPhase: (phase: LoadingPhase | null) => void;
  setStreaming: (v: boolean) => void;
  resetConversation: () => void;
  loadConversation: (id: string) => Promise<void>;
}

let nextId = 0;
const localId = () => `local-${Date.now()}-${++nextId}`;

// Mutate the trailing-streaming assistant message. Kept DRY because
// every streaming handler ends up doing this walk.
function updateLastStreaming(
  msgs: UIMessage[],
  mutator: (m: UIMessage) => UIMessage,
): UIMessage[] {
  const out = [...msgs];
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i].role === 'assistant' && out[i].streaming) {
      out[i] = mutator(out[i]);
      return out;
    }
  }
  return out;
}

// persist() writes (provider, model) to localStorage so a taskpane
// reload doesn't snap the user back to the hardcoded default. The
// server's user_preferences row remains the cross-device source of
// truth — see authStore.hydrate → syncPreferencesToChat.
export const useChatStore = create<ChatState>()(persist((set) => ({
  provider: 'anthropic',
  model: 'claude-sonnet-4-5',
  messages: [],
  conversationId: null,
  streaming: false,
  loadingPhase: null,
  thinkingText: null,
  sessionCostUSD: 0,
  sessionTokens: 0,
  autoBuild: null,
  applyProgress: null,
  pendingAutoContinue: null,
  autoBuildStopSeq: 0,
  retrySeq: 0,
  retryContent: null,

  setProviderModel(provider, model) {
    set({ provider, model });
  },

  setConversation(id) {
    set({ conversationId: id });
  },

  appendUserMessage(content) {
    set((s) => ({
      messages: [
        ...s.messages,
        { id: localId(), role: 'user', content, streaming: false, timestamp: Date.now() },
      ],
    }));
  },

  startAssistantStream() {
    const id = localId();
    set((s) => ({
      streaming: true,
      messages: [
        ...s.messages,
        {
          id,
          role: 'assistant',
          content: '',
          streaming: true,
          timestamp: Date.now(),
          tools: [],
        },
      ],
    }));
    return id;
  },

  appendAssistantDelta(chunk) {
    set((s) => ({
      messages: updateLastStreaming(s.messages, (m) => ({ ...m, content: m.content + chunk })),
    }));
  },

  attachToolCall(p) {
    set((s) => ({
      messages: updateLastStreaming(s.messages, (m) => ({
        ...m,
        tools: [
          ...(m.tools ?? []),
          {
            id: p.id,
            name: p.name,
            category: p.category,
            iteration: p.iteration,
            input: p.input,
            status: 'running',
          } satisfies ToolActivity,
        ],
      })),
    }));
  },

  attachToolResult(p) {
    set((s) => ({
      messages: updateLastStreaming(s.messages, (m) => ({
        ...m,
        tools: (m.tools ?? []).map((t) =>
          t.id === p.id
            ? {
                ...t,
                status: p.is_error ? 'error' : 'done',
                summary: p.summary,
                output: p.output,
                duration_ms: p.duration_ms,
                data: p.data,
              }
            : t,
        ),
      })),
    }));
  },

  // --- Phase 4: diff lifecycle ------------------------------------------

  // attachDiffDelta appends a single change to the assistant's in-flight
  // diff as it's enqueued server-side. Status stays 'streaming' until
  // the finalized set arrives via attachDiff (triggered by diff_ready).
  // The delta may be SUPERSEDED by the finalized set on dedup/merge —
  // attachDiff replaces the whole list, so we don't need to track
  // "drafts" separately.
  attachDiffDelta(p) {
    set((s) => ({
      messages: updateLastStreaming(s.messages, (m) => {
        const existing = m.diff?.changes ?? [];
        // Drop duplicates — same change_id arriving twice can happen if
        // the agent's self-correction loop re-enqueues after a retry.
        if (existing.some((c) => c.id === p.change.id)) {
          return m;
        }
        const nextSelected: Record<string, boolean> = { ...(m.diff?.selected ?? {}) };
        nextSelected[p.change.id] = true;
        return {
          ...m,
          diff: {
            changes: [...existing, p.change],
            dropped: m.diff?.dropped,
            status: 'streaming' as DiffStatus,
            selected: nextSelected,
          },
        };
      }),
    }));
  },

  attachDiff(p) {
    // Empty finalized set = a streamed diff whose changes were all dropped
    // or errored server-side (e.g. "delete the only sheet"). There is
    // NOTHING to apply — so CLEAR the diff card entirely instead of
    // leaving a "0 changes ready / Apply (0)" pending state. The turn then
    // reads as a pure text answer, which is what it is.
    if (!p.changes || p.changes.length === 0) {
      set((s) => ({
        messages: updateLastStreaming(s.messages, (m) => ({ ...m, diff: undefined })),
      }));
      return;
    }

    // Default: every proposed change is pre-selected for the user.
    // Finalized set REPLACES any streamed deltas — the server has
    // applied merges + validation drops, and the final list is the
    // source of truth.
    const selected: Record<string, boolean> = {};
    for (const c of p.changes) selected[c.id] = true;

    set((s) => ({
      messages: updateLastStreaming(s.messages, (m) => ({
        ...m,
        diff: {
          changes: p.changes,
          dropped: p.dropped,
          status: 'pending' as DiffStatus,
          selected,
        },
      })),
    }));
  },

  toggleDiffChange(messageId, changeId) {
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== messageId || !m.diff) return m;
        return {
          ...m,
          diff: {
            ...m.diff,
            selected: {
              ...m.diff.selected,
              [changeId]: !m.diff.selected[changeId],
            },
          },
        };
      }),
    }));
  },

  setDiffAllSelected(messageId, value) {
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== messageId || !m.diff) return m;
        const sel: Record<string, boolean> = {};
        for (const c of m.diff.changes) sel[c.id] = value;
        return { ...m, diff: { ...m.diff, selected: sel } };
      }),
    }));
  },

  setDiffStatus(messageId, status, appliedIds) {
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== messageId || !m.diff) return m;
        const trimmed: ProposedChange[] = appliedIds
          ? m.diff.changes.filter((c) => appliedIds.includes(c.id))
          : m.diff.changes;
        return {
          ...m,
          diff: { ...m.diff, status, changes: trimmed },
        };
      }),
    }));
  },

  // --- Phase 5: clarify flow --------------------------------------------

  attachClarify(p) {
    set((s) => ({
      messages: updateLastStreaming(s.messages, (m) => ({
        ...m,
        clarify: {
          question: p.question,
          options: p.options ?? [],
          status: 'pending',
        },
      })),
    }));
  },

  resolveClarify(messageId, interpretation) {
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== messageId || !m.clarify) return m;
        return {
          ...m,
          clarify: { ...m.clarify, status: 'resolved', resolvedWith: interpretation },
        };
      }),
    }));
  },

  dismissClarify(messageId) {
    set((s) => ({
      messages: s.messages.map((m) => {
        if (m.id !== messageId || !m.clarify) return m;
        return { ...m, clarify: { ...m.clarify, status: 'dismissed' } };
      }),
    }));
  },

  finalizeAssistant(usage, finalId) {
    set((s) => {
      const costDelta = usage?.cost_usd ?? 0;
      const tokenDelta = (usage?.tokens_input ?? 0) + (usage?.tokens_output ?? 0);
      return {
        messages: updateLastStreaming(s.messages, (m) => ({
          ...m,
          // Use `||` not `??` — when the server sends an empty message_id
          // (clarify turns don't persist a message) we must keep the
          // local id. `??` wrongly treats "" as "valid value", causing
          // React key duplicates across multiple clarify turns.
          id: finalId || m.id,
          streaming: false,
          ...(usage ? { usage } : {}),
        })),
        streaming: false,
        loadingPhase: null,
        thinkingText: null,
        sessionCostUSD: s.sessionCostUSD + costDelta,
        sessionTokens: s.sessionTokens + tokenDelta,
      };
    });
  },

  setSuggestions(messageId, items) {
    set((s) => ({
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, suggestions: items } : m)),
    }));
  },

  setMessageFinishReason(messageId, reason) {
    set((s) => ({
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, finishReason: reason } : m)),
    }));
  },

  setAutoBuild(v) {
    set({ autoBuild: v });
  },

  setApplyProgress(v) {
    set({ applyProgress: v });
  },

  setPendingAutoContinue(messageId) {
    set({ pendingAutoContinue: messageId });
  },

  stopAutoBuild() {
    // Bump the nonce so ChatInterface's effect runs its full ref teardown.
    set((s) => ({ autoBuild: null, pendingAutoContinue: null, autoBuildStopSeq: s.autoBuildStopSeq + 1 }));
  },

  requestRetry() {
    set((s) => {
      // Re-send the most recent user message that precedes the errored
      // assistant turn. Drop the failed assistant bubble — the retry
      // starts a fresh stream in its place.
      let content: string | null = null;
      for (let i = s.messages.length - 1; i >= 0; i--) {
        if (s.messages[i].role === 'user') {
          content = s.messages[i].content;
          break;
        }
      }
      if (!content) return s;
      const messages = [...s.messages];
      // Drop the failed assistant bubble and its user message — send()
      // re-appends the user message, so keeping them would duplicate.
      const last = messages[messages.length - 1];
      if (last?.role === 'assistant' && last.error && !last.diff) {
        messages.pop();
        if (messages[messages.length - 1]?.role === 'user') {
          messages.pop();
        }
      }
      return { ...s, messages, retryContent: content, retrySeq: s.retrySeq + 1 };
    });
  },

  failAssistant(message) {
    set((s) => ({
      messages: updateLastStreaming(s.messages, (m) => ({
        ...m,
        streaming: false,
        error: message,
        // Salvage a partially-streamed diff: if changes already arrived
        // via diff_delta (status 'streaming') but the run errored before
        // diff_ready, finalize them to 'pending' so the user can still
        // review/apply the work — instead of a permanently stuck
        // "Waiting…" card. The streamed `selected` set is preserved.
        diff:
          m.diff && m.diff.status === 'streaming' && (m.diff.changes?.length ?? 0) > 0
            ? { ...m.diff, status: 'pending' as DiffStatus }
            : m.diff,
      })),
      streaming: false,
      loadingPhase: null,
      thinkingText: null,
    }));
  },

  setThinking(msg) {
    // Default the loading phase to "thinking" whenever a thinking
    // message arrives WITHOUT an explicit phase already set. Tool
    // calls (which carry a more specific phase) override via setPhase
    // separately. Clearing thinking also clears the phase.
    set((s) => ({
      thinkingText: msg,
      loadingPhase: msg == null ? null : (s.loadingPhase ?? 'thinking'),
    }));
  },

  setPhase(phase) {
    set({ loadingPhase: phase });
  },

  setStreaming(v) {
    set({ streaming: v });
  },

  resetConversation() {
    set({
      messages: [],
      conversationId: null,
      streaming: false,
      loadingPhase: null,
      thinkingText: null,
      sessionCostUSD: 0,
      sessionTokens: 0,
    });
  },

  // loadConversation fetches a past conversation from the server and
  // replaces the in-memory transcript with it. Tool activities, diffs,
  // and clarify state aren't persisted server-side — only role+content
  // survive the reload, so past diff cards won't re-render. Provider +
  // model are kept from the current user selection.
  async loadConversation(id) {
    const detail = await getConversation(id);
    const msgs: UIMessage[] = detail.messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        content: m.content,
        streaming: false,
        timestamp: new Date(m.created_at).getTime(),
      }));
    set({
      conversationId: detail.conversation.id,
      messages: msgs,
      streaming: false,
      thinkingText: null,
      sessionCostUSD: 0,
      sessionTokens: 0,
    });
  },
}), {
  name: 'ubinex.chat',
  version: 2,
  storage: createJSONStorage(() => localStorage),
  // Only persist the model selection. Messages/streaming/diff state
  // must NOT survive a reload — those are tied to the current run.
  partialize: (s) => ({ provider: s.provider, model: s.model }),
  // v1→v2: Claude-only. A returning user may have a stale
  // provider:'openai'/'gemini' in localStorage from the multi-provider
  // era. The backend ignores the client provider (the admin template
  // drives every turn), so it never broke chat — but we force it back to
  // 'anthropic' here so the UI never shows a dead provider.
  migrate: (persisted: unknown, version: number) => {
    const s = (persisted ?? {}) as { provider?: string; model?: string };
    if (version < 2 || s.provider !== 'anthropic') {
      return { ...s, provider: 'anthropic' as Provider };
    }
    return s as { provider: Provider; model: string };
  },
}));
